(() => {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const TAG = 'best-seller';
  const CARD_BADGE_CLASS = 'pv-best-seller-badge';
  const PDP_BADGE_CLASS = 'pv-best-seller-badge--pdp';
  const productCache = new Map();
  let scanQueued = false;

  const normalizeTag = (value) =>
    String(value || '').trim().toLowerCase().replace(/\s+/g, '-');

  const hasBestSellerTag = (tags) =>
    Array.isArray(tags) && tags.some((tag) => normalizeTag(tag) === TAG);

  const getBadgeMediaHost = (badge) => {
    const card = badge.closest('.pv-card, .pv-gc');
    if (card) return card.querySelector('.pv-card__media, .pv-gc__media');

    const host = badge.closest('[data-product-card], .product-card-wrapper, .card-wrapper, .pv-product-card, product-component, li.grid__item, article');
    return host ? host.querySelector('.pv-card__media, .pv-gc__media, .card__media') : null;
  };

  const placeCardBadge = (badge) => {
    if (!badge || badge.classList.contains(PDP_BADGE_CLASS)) return;
    const media = getBadgeMediaHost(badge);
    if (!media || badge.parentElement === media) return;
    media.appendChild(badge);
  };

  const normalizeBestSellerBadges = (scope = document) => {
    const selectors = [
      '.pv-gc__badge--best-seller',
      '.pv-card__flag--best-seller'
    ];

    selectors.forEach((selector) => {
      scope.querySelectorAll(selector).forEach((badge) => {
        badge.classList.remove('pv-gc__badge', 'pv-gc__badge--best-seller', 'pv-card__flag', 'pv-card__flag--best-seller');
        badge.classList.add(CARD_BADGE_CLASS);
        badge.setAttribute('aria-label', 'Best Seller');
        badge.dataset.pvBestSellerBadge = 'true';
        placeCardBadge(badge);
      });
    });

    scope.querySelectorAll('[data-pv-best-seller-badge="true"]:not(.pv-best-seller-badge--pdp)').forEach((badge) => {
      badge.classList.add(CARD_BADGE_CLASS);
      placeCardBadge(badge);
    });
  };

  const getHandleFromHref = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      return null;
    }
  };

  const fetchProduct = async (handle) => {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    const promise = fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      })
      .catch(() => null);

    productCache.set(handle, promise);
    return promise;
  };

  const createBadge = (isPdp = false) => {
    const badge = document.createElement('span');
    badge.className = isPdp
      ? `${CARD_BADGE_CLASS} ${PDP_BADGE_CLASS}`
      : CARD_BADGE_CLASS;
    badge.textContent = 'Best Seller';
    badge.setAttribute('aria-label', 'Best Seller');
    badge.dataset.pvBestSellerBadge = 'true';
    return badge;
  };

  const findCardHost = (link) => {
    const selectors = [
      '[data-product-card]',
      '.product-card-wrapper',
      '.card-wrapper',
      '.pv-card',
      '.pv-product-card',
      'product-component',
      'li.grid__item',
      'article'
    ];

    for (const selector of selectors) {
      const host = link.closest(selector);
      if (host) return host;
    }

    return null;
  };

  const addCardBadge = (host) => {
    if (!host || host.querySelector(`.${CARD_BADGE_CLASS}`)) return;
    host.classList.add('pv-best-seller-host');
    const media = host.querySelector('.pv-card__media, .pv-gc__media, .card__media');
    if (media) media.appendChild(createBadge(false));
  };

  const removeCardBadge = (host) => {
    if (!host) return;
    host.querySelectorAll(`.${CARD_BADGE_CLASS}:not(.${PDP_BADGE_CLASS})`).forEach((badge) => badge.remove());
  };

  const scanProductCards = async () => {
    const links = Array.from(document.querySelectorAll('a[href*="/products/"]'));
    const targets = new Map();

    links.forEach((link) => {
      if (link.closest('.pv-best-seller-badge')) return;
      const handle = getHandleFromHref(link.href);
      const host = findCardHost(link);
      if (!handle || !host) return;
      if (!targets.has(handle)) targets.set(handle, new Set());
      targets.get(handle).add(host);
    });

    await Promise.all(
      Array.from(targets.entries()).map(async ([handle, hosts]) => {
        const product = await fetchProduct(handle);
        const isBestSeller = product ? hasBestSellerTag(product.tags) : false;

        hosts.forEach((host) => {
          if (isBestSeller) addCardBadge(host);
          else removeCardBadge(host);
        });
      })
    );
  };

  const scanProductPage = async () => {
    const pdp = document.querySelector('[data-pv-pdp]');
    if (!pdp) return;

    const title = pdp.querySelector('.pv-pdp__title');
    if (!title) return;

    const handle = getHandleFromHref(window.location.pathname);
    if (!handle) return;

    const product = await fetchProduct(handle);
    const existing = pdp.querySelector(`.${PDP_BADGE_CLASS}`);

    if (product && hasBestSellerTag(product.tags)) {
      if (!existing) title.parentNode.insertBefore(createBadge(true), title);
    } else if (existing) {
      existing.remove();
    }
  };

  const scan = () => {
    if (scanQueued) return;
    scanQueued = true;

    window.requestAnimationFrame(() => {
      scanQueued = false;
      normalizeBestSellerBadges(document);
      scanProductCards();
      scanProductPage();
    });
  };

  const observer = new MutationObserver(scan);

  const init = () => {
    normalizeBestSellerBadges(document);
    scan();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:reorder', scan);
})();

/* PEVELL add-on: keep Best Sellers in the existing pending filter/count UI.
   This only reconciles the Highlights count and the global filter badges.
   It does not commit the URL, change product filtering, or alter other filters. */
(() => {
  'use strict';

  const initBestSellerFilterCount = () => {
    const root = document.querySelector('.pv-coll-wrap');
    if (!root || root.dataset.pvBestSellerFilterCountBound === 'true') return;
    root.dataset.pvBestSellerFilterCountBound = 'true';

    const getHighlightCount = () =>
      root.querySelectorAll('#pvDrawer [data-pv-highlight].is-active').length ||
      root.querySelectorAll('#pvDrawer [data-pv-highlight][aria-pressed="true"]').length;

    const getFilterBadges = () => {
      const badges = new Set(
        root.querySelectorAll('.pv-drawer__badge, .pv-topbar__filter [class*="badge"], .pv-topbar__filter [class*="count"]')
      );
      return Array.from(badges);
    };

    let previousHighlightCount = getHighlightCount();

    const syncBestSellerFilterCount = () => {
      const currentHighlightCount = getHighlightCount();

      const tabCount = root.querySelector('[data-pv-tab-count="highlights"]');
      if (tabCount) {
        tabCount.textContent = currentHighlightCount ? `(${currentHighlightCount})` : '';
        tabCount.hidden = currentHighlightCount === 0;
      }

      getFilterBadges().forEach((badge) => {
        const currentTotal = Math.max(0, parseInt(badge.textContent || '0', 10) || 0);
        const otherFiltersCount = Math.max(0, currentTotal - previousHighlightCount);
        const desiredTotal = otherFiltersCount + currentHighlightCount;

        if (badge.textContent !== String(desiredTotal)) {
          badge.textContent = String(desiredTotal);
        }
      });

      previousHighlightCount = currentHighlightCount;
    };

    root.addEventListener('click', (event) => {
      const highlight = event.target.closest('#pvDrawer [data-pv-highlight]');
      if (!highlight) return;

      requestAnimationFrame(syncBestSellerFilterCount);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBestSellerFilterCount, { once: true });
  } else {
    initBestSellerFilterCount();
  }
})();